// Import polyfills first
import "core-js/actual/array/at";
import "./polyfills";

// Migrate deprecated URL search params to their canonical snake_case equivalents
// as early as possible, before any other code reads window.location.search.
import { migrateDeprecatedParams } from "@/libs/url";

migrateDeprecatedParams();

import React, { StrictMode, useEffect, useState } from "react";

import {
  browserTracingIntegration,
  ErrorBoundary,
  init,
  makeBrowserOfflineTransport,
  makeFetchTransport,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
  setTag,
  withSentryReactRouterV6Routing,
} from "@sentry/react";
import { createRoot } from "react-dom/client";
import { MapProvider } from "react-map-gl/maplibre";
import { Provider } from "react-redux";
import {
  createRoutesFromChildren,
  matchRoutes,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { setIsServiceWorkerRegistered } from "./features/app/appSlice";

import { GeoJSONEditorProvider } from "@/features/geojson_editor/GeoJSONEditorProvider";
import reportHandler from "@/reportHandler";
import reportWebVitals from "@/reportWebVitals";
import { version } from "../package.json";
import { useAppDispatch } from "./app/hooks";
import useServiceWorker from "./app/hooks/useServiceWorker";
import { configStore } from "./app/store";
import { SENTRY_DSN } from "./constants";
import App from "./features/app/App";
import NotificationHandler from "./features/app/NotificationHandler";
import { PopupMenuProvider } from "./features/app/PopupMenuProvider";
import BottomDrawer from "./features/bottom_drawer/BottomDrawer";
import { BottomDrawerProvider } from "./features/bottom_drawer/BottomDrawerContext";
import SentryErrorPage from "./features/cartography/SentryErrorPage";
import { DeckGlMapProvider } from "./features/map/DeckGlMapProvider";
import { MapInteractionsProvider } from "./features/map/MapInteractionsProvider";
import { NativeRoutesProvider } from "./features/native_routes/NativeRoutesProvider";
import { SitumAPIProvider } from "./features/situm_api/SitumAPIProvider";
import { WorkerProvider } from "./features/worker/WorkerProvider";

import "maplibre-gl/dist/maplibre-gl.css";
import "./app/i18n";

// Report Web Vitals
reportWebVitals(reportHandler);

if (SENTRY_DSN) {
  init({
    dsn: SENTRY_DSN,
    ignoreErrors: [/Job rejected for non app-bound domain/],
    integrations: [
      browserTracingIntegration(),
      reactRouterV6BrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
      }),
      replayIntegration({
        blockAllMedia: true,
        // Additional SDK configuration goes in here, for example:
        maskAllText: true,
      }),
    ],
    release: "map-viewer@" + version,
    replaysOnErrorSampleRate: 0.05,
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.01,
    transport: makeBrowserOfflineTransport(makeFetchTransport),
  });
  setTag("map_viewer_version", version);
} else console.debug("Sentry DSN not configured");

// Reload page if vite:preloadError event is detected
window.addEventListener("vite:preloadError", (event) => {
  console.warn("vite:preloadError event detected", event);
  // window.location.reload();
});

const AppWithServiceWorker: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const [swRegistrationFinished, setSwRegistrationFinished] = useState(false);
  const { registerServiceWorker } = useServiceWorker();

  useEffect(() => {
    registerServiceWorker({
      onFinish: ({ success }) => {
        if (success) {
          console.debug("[SW] Registered succesfully");
        }
        dispatch(setIsServiceWorkerRegistered(success));
        setSwRegistrationFinished(true);
      },
    });
  }, []);

  if (!swRegistrationFinished) {
    return null;
  }

  return children;
};

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

const rootElement = document.getElementById("root");
// biome-ignore lint/style/noNonNullAssertion: Root is never null
const root = createRoot(rootElement!);

root.render(
  <StrictMode>
    <Provider store={configStore}>
      <ErrorBoundary
        fallback={({ error, resetError }) => {
          return (
            <SentryErrorPage error={error} resetErrorBoundary={resetError} />
          );
        }}
      >
        <AppWithServiceWorker>
          <SitumAPIProvider>
            <WorkerProvider>
              <NativeRoutesProvider>
                <BottomDrawerProvider>
                  <PopupMenuProvider>
                    <MapProvider>
                      <DeckGlMapProvider>
                        <MapInteractionsProvider>
                          <GeoJSONEditorProvider>
                            <BottomDrawer />
                            <NotificationHandler />
                            <Router>
                              <SentryRoutes>
                                <Route path="/" element={<App />} />
                                <Route
                                  path="/id/:config_file"
                                  element={<App />}
                                />
                                <Route path="/:config_file" element={<App />} />
                              </SentryRoutes>
                            </Router>
                          </GeoJSONEditorProvider>
                        </MapInteractionsProvider>
                      </DeckGlMapProvider>
                    </MapProvider>
                  </PopupMenuProvider>
                </BottomDrawerProvider>
              </NativeRoutesProvider>
            </WorkerProvider>
          </SitumAPIProvider>
        </AppWithServiceWorker>
      </ErrorBoundary>
    </Provider>
  </StrictMode>,
);
